import Dashboard from "layouts/dashboard";
import Tables from "layouts/tables";
import Billing from "layouts/billing";
import Profile from "layouts/profile";
import SignIn from "layouts/authentication/sign-in";
import SignUp from "layouts/authentication/sign-up";
import SlackCallback from "layouts/authentication/slack-callback";
import OauthAuthorize from "layouts/oauthAuthorize";
import Shop from "examples/Icons/Shop";
import Office from "examples/Icons/Office";
import CustomerSupport from "examples/Icons/CustomerSupport";
import CreditCard from "examples/Icons/CreditCard";
import Document from "examples/Icons/Document";
import Settings from "examples/Icons/Settings";
import Reports from "layouts/reports";
import GeneralReport from "layouts/reports/components/GeneralReport";
import LeaveAndWFHReportContent from "layouts/leaveAndWFHReport/components/LeaveAndWFHReportContent";
import TravelAllowanceReport from "layouts/reports/components/TravelAllowanceReport";
import UnresolvedComments from "layouts/reports/components/UnresolvedComments";
import SettingsLayout from "layouts/settings";
import PublicHolidays from "layouts/settings/components/PublicHolidays";
import Goals from "layouts/goals";
import EngineerGoals from "layouts/goals/engineer";
import GoalTemplates from "layouts/goals/templates";
import LeaveRequests from "layouts/leaveRequests";
import Icon from "@mui/material/Icon";
import UserManagement from "layouts/userManagement";
import Roles from "layouts/userManagement/roles";
import Employees from "layouts/employees";
import Assets from "layouts/assets";
import Organizations from "layouts/organizations";
import AccountProfile from "layouts/account";
import { PERMISSIONS } from "utils/permissions";

const routes = [
  {
    type: "collapse",
    name: "Dashboard",
    key: "",
    permission: PERMISSIONS.DASHBOARD,
    route: "/",
    icon: <Shop size="12px" />,
    component: <Dashboard />,
    noCollapse: true,
    privacy: "private",
  },
  {
    type: "collapse",
    name: "Requests",
    key: "requests",
    permission: PERMISSIONS.REQUESTS,
    route: "/requests",
    icon: <Icon fontSize="small">event_note</Icon>,
    component: <LeaveRequests />,
    noCollapse: true,
    privacy: "private",
  },
  {
    type: "collapse",
    name: "Reports",
    key: "reports",
    route: "/reports",
    icon: <Document size="12px" />,
    component: <Reports />,
    noCollapse: true,
    privacy: "private",
    nested: [
      {
        path: "general-report",
        component: <GeneralReport />,
        key: "reports-general-report",
        permission: PERMISSIONS.REPORTS_GENERAL,
      },
      {
        path: "leave-and-wfh-report",
        component: <LeaveAndWFHReportContent />,
        key: "reports-leave-and-wfh-report",
        permission: PERMISSIONS.REPORTS_LEAVE_WFH,
      },
      {
        path: "travel-allowance-report",
        component: <TravelAllowanceReport />,
        key: "reports-travel-allowance-report",
        permission: PERMISSIONS.REPORTS_TRAVEL_ALLOWANCE,
      },
      {
        path: "unresolved-comments",
        component: <UnresolvedComments />,
        key: "reports-unresolved-comments",
        permission: PERMISSIONS.REPORTS_UNRESOLVED_COMMENTS,
      },
    ],
  },
  {
    type: "collapse",
    name: "Employees",
    key: "employees",
    // Doesn't need a Slack workspace or channel.
    worksWithoutWorkspace: true,
    route: "/employees",
    icon: <Icon fontSize="small">badge</Icon>,
    component: <Employees />,
    noCollapse: true,
    privacy: "private",
    permission: PERMISSIONS.EMPLOYEES_MANAGE,
  },
  {
    type: "collapse",
    name: "Goals",
    key: "goals",
    permission: PERMISSIONS.GOALS,
    route: "/goals",
    icon: <Icon fontSize="small">flag</Icon>,
    component: <Goals />,
    noCollapse: true,
    privacy: "private",
  },
  {
    // Not a sidenav item (type isn't "collapse") - reached from the Goals table.
    type: "",
    name: "Engineer Goals",
    key: "goals-engineer",
    permission: PERMISSIONS.GOALS,
    route: "/goals/:userChannelId",
    component: <EngineerGoals />,
    noCollapse: true,
    privacy: "private",
  },
  {
    // Static segment, so it wins over "/goals/:userChannelId" above.
    type: "",
    name: "Goal Templates",
    key: "goals-templates",
    permission: PERMISSIONS.GOALS,
    route: "/goals/templates",
    component: <GoalTemplates />,
    noCollapse: true,
    privacy: "private",
  },
  {
    type: "collapse",
    name: "Assets",
    key: "assets",
    // Doesn't need a Slack workspace or channel.
    worksWithoutWorkspace: true,
    route: "/assets",
    icon: <Icon fontSize="small">devices</Icon>,
    component: <Assets />,
    noCollapse: true,
    privacy: "private",
    permission: PERMISSIONS.ASSETS_MANAGE,
  },
  {
    type: "collapse",
    name: "Users",
    key: "users",
    // Doesn't need a Slack workspace or channel.
    worksWithoutWorkspace: true,
    route: "/users",
    icon: <Icon fontSize="small">manage_accounts</Icon>,
    component: <UserManagement />,
    noCollapse: true,
    privacy: "private",
    permission: PERMISSIONS.USERS_MANAGE,
  },
  {
    // Not a sidenav item: reached from the Roles button on the Users page.
    type: "",
    name: "Roles",
    key: "users-roles",
    // Doesn't need a Slack workspace or channel.
    worksWithoutWorkspace: true,
    permission: PERMISSIONS.USERS_MANAGE,
    // Only Super Admins and platform admins.
    superAdminOnly: true,
    route: "/users/roles",
    component: <Roles />,
    noCollapse: true,
    privacy: "private",
  },
  {
    type: "collapse",
    name: "Settings",
    key: "settings",
    // Doesn't need a Slack workspace or channel.
    worksWithoutWorkspace: true,
    route: "/settings",
    icon: <Settings size="12px" />,
    component: <SettingsLayout />,
    noCollapse: true,
    privacy: "private",
    nested: [
      {
        path: "public-holidays",
        component: <PublicHolidays />,
        key: "settings-public-holidays",
        // Doesn't need a Slack workspace or channel.
        worksWithoutWorkspace: true,
        permission: PERMISSIONS.SETTINGS_PUBLIC_HOLIDAYS,
      },
    ],
  },
  { type: "divider", key: "account-divider" },
  {
    type: "collapse",
    name: "Organizations",
    key: "organizations",
    // Doesn't need a Slack workspace or channel.
    worksWithoutWorkspace: true,
    route: "/organizations",
    icon: <Icon fontSize="small">apartment</Icon>,
    component: <Organizations />,
    noCollapse: true,
    privacy: "private",
    // Platform admins only (a flag on the account, not a role permission).
    platformAdminOnly: true,
  },
  {
    // No `permission`: every signed-in admin can open their own profile.
    type: "collapse",
    name: "Profile",
    key: "profile",
    // Doesn't need a Slack workspace or channel.
    worksWithoutWorkspace: true,
    route: "/profile",
    icon: <Icon fontSize="small">person</Icon>,
    component: <AccountProfile />,
    noCollapse: true,
    privacy: "private",
  },
  // {
  //   type: "collapse",
  //   name: "Leave And WFH Report",
  //   key: "leaveAndWFHReport",
  //   route: "/leaveAndWFHReport",
  //   icon: <Document size="12px" />,
  //   component: <LeaveAndWFHReport />,
  //   noCollapse: true,
  //   privacy: "private",
  // },
  // {
  //   type: "collapse",
  //   name: "Tables",
  //   key: "tables",
  //   route: "/tables",
  //   icon: <Office size="12px" />,
  //   component: <Tables />,
  //   noCollapse: true,
  //   privacy: "private",
  // },
  // {
  //   type: "collapse",
  //   name: "Billing",
  //   key: "billing",
  //   route: "/billing",
  //   icon: <CreditCard size="12px" />,
  //   component: <Billing />,
  //   noCollapse: true,
  //   privacy: "private",
  // },
  // { type: "title", title: "Account Pages", key: "account-pages" },
  // {
  //   type: "collapse",
  //   name: "Profile",
  //   key: "profile",
  //   route: "/profile",
  //   icon: <CustomerSupport size="12px" />,
  //   component: <Profile />,
  //   noCollapse: true,
  //   privacy: "private",
  // },
  {
    type: "",
    name: "Sign In",
    key: "sign-in",
    route: "/authentication/sign-in",
    icon: <CustomerSupport size="12px" />,
    component: <SignIn />,
    noCollapse: false,
    privacy: "public",
  },
  {
    type: "",
    name: "Slack Sign In",
    key: "slack-sign-in-callback",
    route: "/authentication/slack/callback",
    icon: <CustomerSupport size="12px" />,
    component: <SlackCallback />,
    noCollapse: false,
    privacy: "public",
  },
  {
    type: "",
    name: "Authorize",
    key: "oauth-authorize",
    route: "/oauth/authorize",
    icon: <CustomerSupport size="12px" />,
    component: <OauthAuthorize />,
    noCollapse: false,
    // Neither "private" nor "public" — both PrivateRoute and PublicRoute
    // redirect based on login state and drop the query string doing it,
    // which would lose the whole OAuth request (client_id, redirect_uri,
    // code_challenge, ...). This page handles both the logged-out (show
    // sign-in) and logged-in (show consent) cases itself. See App.js.
    privacy: "oauth",
  },
  // {
  //   type: "",
  //   name: "Sign Up",
  //   key: "sign-up",
  //   route: "/authentication/sign-up",
  //   icon: <CustomerSupport size="12px" />,
  //   component: <SignUp />,
  //   noCollapse: false,
  //   privacy: "public",
  // },
];

export default routes;
